import React, { useState, useRef, useEffect, useMemo } from "react";
import { VariantProps, cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
import { Check, ChevronDown, X, Search, Plus } from "lucide-react";
import { FixedSizeList as List } from "react-window";
import { useTranslation } from "react-i18next";

export interface SelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps extends VariantProps<typeof multiSelectVariants> {
  options: SelectOption[];
  value: SelectOption[];
  onChange: (value: SelectOption[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowCustomOptions?: boolean;
  label?: string;
  icon?: React.ElementType;
  multiple?: boolean;
  defaultValue?: SelectOption[];
}

const multiSelectVariants = cva("relative w-full text-sm", {
  variants: {
    size: {
      default: "w-full",
      sm: "w-64",
      lg: "w-96",
    },
    status: {
      default: "",
      error: "",
      success: "",
    },
  },
  defaultVariants: {
    size: "default",
    status: "default",
  },
});

const dropdownButtonStyles = cva(
  "flex items-center justify-between w-full px-3 py-2.5 border rounded-lg shadow-sm transition-all duration-200",
  {
    variants: {
      status: {
        default:
          "border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-400 focus:outline-none focus:border-primary-500 dark:focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-primary-400/20",
        error: "border-red-300 focus:ring-red-500/20 focus:border-red-500",
        success:
          "border-green-300 focus:ring-green-500/20 focus:border-green-500",
      },
      disabled: {
        true: "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed",
        false: "text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800",
      },
    },
    defaultVariants: {
      status: "default",
      disabled: false,
    },
  },
);

export const MultiSelectSearch: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select options...",
  className,
  disabled = false,
  allowCustomOptions = false,
  label,
  icon: Icon,
  multiple = true,
  size,
  status,
  defaultValue = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">(
    "bottom",
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.label
        .toLocaleUpperCase("tr-TR")
        .includes(searchValue.toLocaleUpperCase("tr-TR")),
    );
  }, [options, searchValue]);

  const showAddCustom = useMemo(() => {
    if (!allowCustomOptions || searchValue.trim() === "") return false;

    return !options.some(
      (option) =>
        option.label.toLocaleUpperCase("tr-TR") ===
        searchValue.toLocaleUpperCase("tr-TR"),
    );
  }, [allowCustomOptions, searchValue, options]);

  useEffect(() => {
    if (defaultValue.length > 0 && value.length === 0) {
      onChange(defaultValue);
    }
  }, [defaultValue, value, onChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchValue("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // New effect to check dropdown position when it's about to open
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const buttonRect = dropdownRef.current.getBoundingClientRect();
      const dropdownHeight = Math.min(
        (filteredOptions.length + (showAddCustom ? 1 : 0)) * 36 + 55, // 55px for search box area
        255, // Maximum height (200px list + 55px search)
      );

      const spaceBelow = window.innerHeight - buttonRect.bottom;

      // If there's not enough space below, position the dropdown above
      if (spaceBelow < dropdownHeight && buttonRect.top > dropdownHeight) {
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }
    }
  }, [isOpen, filteredOptions.length, showAddCustom]);

  const toggleOption = (option: SelectOption) => {
    if (multiple) {
      const isSelected = value.some((item) => item.value === option.value);

      if (isSelected) {
        onChange(value.filter((item) => item.value !== option.value));
      } else {
        onChange([...value, option]);
      }
    } else {
      onChange([option]);
      setIsOpen(false);
      setSearchValue("");
    }
  };

  const removeOption = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((item) => item.value !== optionValue));
  };

  const addCustomOption = () => {
    if (!searchValue.trim()) return;

    const newOption: SelectOption = {
      value: `custom-${searchValue.trim().toLocaleUpperCase("tr-TR").replace(/\s+/g, "-")}`,
      label: searchValue.trim(),
    };

    if (multiple) {
      onChange([...value, newOption]);
    } else {
      onChange([newOption]);
      setIsOpen(false);
    }

    setSearchValue("");
  };

  const isOptionSelected = (option: SelectOption) => {
    return value.some((item) => item.value === option.value);
  };

  const OptionRow = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    if (showAddCustom && index === 0) {
      return (
        <div
          style={style}
          className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
          onClick={addCustomOption}
        >
          <Plus className="w-4 h-4 mr-2 text-primary-500 dark:text-primary-400" />
          <span className="text-gray-700 dark:text-gray-300">
            Add "{searchValue}"
          </span>
        </div>
      );
    }

    const optionIndex = showAddCustom ? index - 1 : index;
    const option = filteredOptions[optionIndex];

    if (!option) return null;

    return (
      <div
        style={style}
        className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
          isOptionSelected(option)
            ? "bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400"
            : "text-gray-700 dark:text-gray-300"
        }`}
        onClick={() => toggleOption(option)}
      >
        <span>{t(option.label)}</span>
        {isOptionSelected(option) && (
          <Check className="w-4 h-4 text-primary-500 dark:text-primary-400" />
        )}
      </div>
    );
  };

  return (
    <div className={twMerge(multiSelectVariants({ size, status, className }))}>
      {label && (
        <label
          className={`block text-sm font-medium mb-1 ${
            disabled
              ? "text-gray-400 dark:text-gray-500"
              : "text-gray-700 dark:text-gray-300"
          }`}
        >
          {label}
        </label>
      )}

      <div ref={dropdownRef} className="relative">
        <div
          className={twMerge(
            dropdownButtonStyles({
              status,
              disabled,
              className: `group ${isOpen ? "ring-2 ring-primary-500/20 dark:ring-primary-400/20 border-primary-500 dark:border-primary-400" : ""}`,
            }),
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex flex-grow flex-wrap items-center gap-1 min-h-6">
            {Icon && (
              <div className="mr-2">
                <Icon
                  className={`h-5 w-5 transition-colors ${
                    disabled
                      ? "text-gray-300 dark:text-gray-600"
                      : "text-gray-400 dark:text-gray-500 group-focus-within:text-primary-500 dark:group-focus-within:text-primary-400"
                  }`}
                />
              </div>
            )}

            {value.length === 0 ? (
              <span className="text-gray-400 dark:text-gray-500">
                {t(placeholder)}
              </span>
            ) : multiple ? (
              <div className="flex flex-wrap gap-1 max-w-full overflow-hidden">
                {value.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-md text-sm"
                  >
                    <span className="truncate max-w-xs">{t(option.label)}</span>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={(e) => removeOption(option.value, e)}
                        className="ml-1 text-primary-500 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-gray-900 dark:text-gray-100">
                {t(value[0]?.label)}
              </span>
            )}
          </div>

          <ChevronDown
            className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${
              isOpen ? "transform rotate-180" : ""
            }`}
          />
        </div>

        {isOpen && (
          <div
            className={`absolute z-50 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden ${
              dropdownPosition === "top" ? "bottom-full mb-1" : "top-full mt-1"
            }`}
          >
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-8 pr-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-primary-400/20 focus:border-primary-500 dark:focus:border-primary-400"
                />
              </div>
            </div>

            <div>
              {filteredOptions.length === 0 && !showAddCustom ? (
                <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                  {searchValue ? "No matching options" : "No options available"}
                </div>
              ) : (
                <List
                  height={36 * 5} // Fixed height to show exactly 5 items
                  itemCount={filteredOptions.length + (showAddCustom ? 1 : 0)}
                  itemSize={36}
                  width="100%"
                >
                  {OptionRow}
                </List>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectSearch;
